import { useEffect, useRef, useState } from "react";
import { useBluelagoon } from "@bluelagoon";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@shadcn-in-nx/ui/dialog";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";
import { Skeleton } from "@shadcn-in-nx/ui/skeleton";
import { Search, User, UserMinus, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 300;

const useDebounced = (value, ms) => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = setTimeout(() => setDebounced(value), ms);
        return () => clearTimeout(id);
    }, [value, ms]);
    return debounced;
};

export const DialogManageMembers = ({ group, actAsAdmin, onChange, trigger }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [memberSearch, setMemberSearch] = useState("");
    const [userSearch, setUserSearch] = useState("");
    const debouncedMember = useDebounced(memberSearch, DEBOUNCE_MS);
    const debouncedUser = useDebounced(userSearch, DEBOUNCE_MS);
    const refetchTokenRef = useRef(0);

    const {
        call: getGroupMembers,
        response: membersResponse,
        isLoading: loadingMembers,
    } = useBluelagoon({ method: "getGroupMembers", resetResponse: false });
    const {
        call: getUsers,
        response: usersResponse,
        isLoading: loadingUsers,
    } = useBluelagoon({ method: "getUsers", resetResponse: false });
    const { call: addMember, isLoading: adding } = useBluelagoon({
        method: "addMemberToGroup",
    });
    const { call: removeMember, isLoading: removing } = useBluelagoon({
        method: "removeMemberFromGroup",
    });

    const [refetchToken, setRefetchToken] = useState(0);

    const refetchMembers = () => {
        refetchTokenRef.current += 1;
        setRefetchToken(refetchTokenRef.current);
    };

    useEffect(() => {
        if (!open) return;
        getGroupMembers({
            groupId: group.id,
            search: debouncedMember,
            limit: PAGE_SIZE,
            offset: 0,
            companyOnly: actAsAdmin || undefined,
        }).catch((err) => console.error("Failed to fetch members:", err));
    }, [open, debouncedMember, getGroupMembers, group.id, actAsAdmin, refetchToken]);

    useEffect(() => {
        if (!open) return;
        getUsers({
            search: debouncedUser,
            limit: PAGE_SIZE,
            offset: 0,
            companyOnly: actAsAdmin || undefined,
            excludeGroupId: group.id,
        }).catch((err) => console.error("Failed to fetch users:", err));
    }, [open, debouncedUser, getUsers, group.id, actAsAdmin, refetchToken]);

    const handleAdd = async (memberUserId) => {
        try {
            await addMember({ groupId: group.id, memberUserId });
            toast.success(t("groups.memberAdded"));
            refetchMembers();
            onChange?.();
        } catch (err) {
            toast.error(err?.message || t("groups.failedToAddMember"));
        }
    };

    const handleRemove = async (memberUserId) => {
        try {
            await removeMember({ groupId: group.id, memberId: memberUserId });
            toast.success(t("groups.memberRemoved"));
            refetchMembers();
            onChange?.();
        } catch (err) {
            toast.error(err?.message || t("groups.failedToRemoveMember"));
        }
    };

    const members = membersResponse?.data?.members || [];
    const eligibleUsers = usersResponse?.users || [];
    const memberTotal = membersResponse?.data?.pagination?.total ?? 0;

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>{trigger}</DialogTrigger>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{t("groups.manageMembers")}</DialogTitle>
                    <DialogDescription>
                        {t("groups.manageMembersDescription", {
                            name: group.name,
                        })}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                    <section>
                        <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium">
                                {t("groups.currentMembers")}
                            </h4>
                            <span className="text-xs text-gray-500">
                                {t("groups.memberCount", { count: memberTotal })}
                            </span>
                        </div>
                        <div className="relative mb-2">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <Input
                                type="text"
                                placeholder={t(
                                    "groups.searchMembersPlaceholder"
                                )}
                                value={memberSearch}
                                onChange={(e) =>
                                    setMemberSearch(e.target.value)
                                }
                                className="pl-10"
                            />
                        </div>
                        <div className="space-y-2 max-h-56 overflow-y-auto">
                            {loadingMembers && !membersResponse ? (
                                [...Array(3)].map((_, i) => (
                                    <Skeleton
                                        key={`m-skel-${i}`}
                                        className="h-10 w-full"
                                    />
                                ))
                            ) : members.length ? (
                                members.map((member) => (
                                    <div
                                        key={member.id}
                                        className="flex items-center justify-between p-2 border rounded"
                                    >
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4" />
                                            <span>
                                                {member.user?.displayName}
                                            </span>
                                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                                {member.role}
                                            </span>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-red-600 hover:text-red-700"
                                            onClick={() =>
                                                handleRemove(member.user?.id)
                                            }
                                            disabled={removing}
                                        >
                                            <UserMinus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))
                            ) : (
                                <p className="text-gray-500">
                                    {t("groups.noMembers")}
                                </p>
                            )}
                        </div>
                    </section>
                    <section>
                        <h4 className="font-medium mb-2">
                            {t("groups.addMembers")}
                        </h4>
                        <div className="relative mb-2">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <Input
                                type="text"
                                placeholder={t("groups.searchUsersPlaceholder")}
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {loadingUsers && !usersResponse ? (
                                [...Array(3)].map((_, i) => (
                                    <Skeleton
                                        key={`u-skel-${i}`}
                                        className="h-10 w-full"
                                    />
                                ))
                            ) : eligibleUsers.length ? (
                                eligibleUsers.map((u) => (
                                    <div
                                        key={u.id}
                                        className="flex items-center justify-between p-2 border rounded"
                                    >
                                        <div className="flex items-center gap-2">
                                            <User className="w-4 h-4" />
                                            <span>{u.displayName}</span>
                                            <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                                                {u.department}
                                            </span>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-green-600 hover:text-green-700"
                                            onClick={() => handleAdd(u.id)}
                                            disabled={adding}
                                        >
                                            <UserPlus className="w-4 h-4" />
                                        </Button>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-gray-500 py-2">
                                    {t("groups.noUsersAvailable")}
                                </p>
                            )}
                        </div>
                    </section>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default DialogManageMembers;
